import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useReducer, useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { Backdrop, Box, Button, Fade, Modal, Stack, Typography } from '@mui/material';
import { HeaderComponent } from './components/HeaderComponents';
import { GameState } from './Types';
import { singleGameReducerFactory, getNewSingleGameFactory, SingleGameActionType } from './SingleGameReducer';
import { DoubleGameComponent } from './components/DoubleGameComponent';
import { solutionWordsEnglish } from './data/solutionWords';
import { isEnglishWordValid, isSpanishWordValid } from './data/Words';
import { solutionWordsSpanish } from './data/spanishSolutionWords';
import { readGame, readGameType, storeGame, storeGameType } from './GameStorage';
import { GameType } from './Types';
import CloseIcon from '@mui/icons-material/Close';
const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};
const NewPracticeModal = ({ open, handleClose, handleNewPracticeGame, won, leftWord, rightWord }) => {
    return (_jsx(Modal, { "aria-labelledby": "transition-modal-title", "aria-describedby": "transition-modal-description", open: open, onClose: (_, reason) => {
            if (reason !== 'backdropClick') {
                handleClose();
            }
        }, disableEscapeKeyDown: true, closeAfterTransition: true, BackdropComponent: Backdrop, BackdropProps: {
            timeout: 500,
        }, children: _jsx(Fade, { in: open, children: _jsxs(Box, { sx: {
                    position: "absolute",
                    top: "20%",
                    left: "50%",
                    transform: 'translate(-50%, -50%)',
                    width: "350px",
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 0,
                    bgcolor: 'background.paper',
                }, children: [_jsx(Stack, { sx: {
                            marginTop: "4px",
                            marginRight: "4px",
                        }, direction: "row-reverse", children: _jsx(CloseIcon, { sx: {
                                display: "none",
                            }, onClick: handleClose }) }), _jsxs(Stack, { sx: {
                            margin: "10px",
                        }, direction: "column", children: [won ? _jsxs(Box, { children: [_jsx(Typography, { variant: "h6", component: "h2", children: "You won!" }), _jsx(Typography, { sx: { mt: 2 }, children: "You should definitely try another one." })] })
                                : _jsxs(Box, { children: [_jsx(Typography, { variant: "h6", component: "h2", children: "You lost!" }), _jsxs(Typography, { sx: { mt: 2 }, children: ["The words were ", leftWord, " and ", rightWord, ". Try again?"] })] }), _jsx(Button, { onClick: handleNewPracticeGame, children: "New Practice Game" })] })] }) }) }));
};
function App() {
    const [leftGame, dispatchLeftGameAction] = useReducer(singleGameReducerFactory(solutionWordsSpanish, isSpanishWordValid), undefined, getNewSingleGameFactory(solutionWordsSpanish));
    const [rightGame, dispatchRightGameAction] = useReducer(singleGameReducerFactory(solutionWordsEnglish, isEnglishWordValid), undefined, getNewSingleGameFactory(solutionWordsEnglish));
    const [gameType, setGameType] = React.useState(null);
    const [practiceModalOpen, setPracticeModalOpen] = useState(false);
    const previousGameType = usePrevious(gameType);
    const handlePracticeModalClose = useCallback(() => {
        setPracticeModalOpen(false);
    }, [setPracticeModalOpen]);
    const handlePracticeAction = useCallback(() => {
        setPracticeModalOpen(!practiceModalOpen);
    }, [setPracticeModalOpen]);
    const newPracticeGameListener = useCallback(() => {
        setGameType(GameType.practice);
        if (leftGame.state !== GameState.playing && rightGame.state !== GameState.playing) {
            if (gameType === GameType.practice) {
                dispatchLeftGameAction({
                    type: SingleGameActionType.new
                });
                dispatchRightGameAction({
                    type: SingleGameActionType.new
                });
            }
            else {
                setGameType(GameType.practice);
            }
        }
        setPracticeModalOpen(false);
    }, [leftGame.state, rightGame.state, gameType]);
    const dailyGameListener = useCallback(() => {
        setGameType(GameType.daily);
    }, []);
    useEffect(() => {
        try {
            const storedGameType = readGameType();
            setGameType(storedGameType);
        }
        catch {
            setGameType(GameType.practice);
        }
    }, []);
    useEffect(() => {
        if (previousGameType !== gameType) {
            return;
        }
        if (gameType === GameType.practice) {
            storeGame('leftPracticeGame', leftGame);
        }
        if (gameType === GameType.daily) {
            storeGame('leftDailyGame', leftGame);
        }
    }, [leftGame, gameType, previousGameType]);
    useEffect(() => {
        if (previousGameType !== gameType) {
            return;
        }
        if (gameType === GameType.practice) {
            storeGame('rightPracticeGame', rightGame);
        }
        if (gameType === GameType.daily) {
            storeGame('rightDailyGame', rightGame);
        }
    }, [rightGame, gameType, previousGameType]);
    useEffect(() => {
        if (gameType !== null) {
            storeGameType(gameType);
            if (gameType === GameType.practice) {
                try {
                    const newLeftGame = readGame('leftPracticeGame');
                    dispatchLeftGameAction({
                        type: SingleGameActionType.initializeFromState,
                        payload: newLeftGame
                    });
                }
                catch {
                    dispatchLeftGameAction({
                        type: SingleGameActionType.new
                    });
                }
                try {
                    const newRightGame = readGame('rightPracticeGame');
                    dispatchRightGameAction({
                        type: SingleGameActionType.initializeFromState,
                        payload: newRightGame,
                    });
                }
                catch {
                    dispatchRightGameAction({
                        type: SingleGameActionType.new
                    });
                }
            }
            else if (gameType === GameType.daily) {
                try {
                    const newLeftGame = readGame('leftDailyGame');
                    dispatchLeftGameAction({
                        type: SingleGameActionType.initializeFromState,
                        payload: newLeftGame
                    });
                }
                catch {
                    dispatchLeftGameAction({
                        // TODO: trigger daily game action
                        type: SingleGameActionType.new
                    });
                }
                try {
                    const newRightGame = readGame('rightDailyGame');
                    dispatchRightGameAction({
                        type: SingleGameActionType.initializeFromState,
                        payload: newRightGame,
                    });
                }
                catch {
                    dispatchRightGameAction({
                        // TODO: trigger daily game action
                        type: SingleGameActionType.new
                    });
                }
            }
        }
    }, [gameType, dispatchLeftGameAction, dispatchRightGameAction]);
    useEffect(() => {
        if (leftGame.currentGuessNumber < rightGame.currentGuessNumber) {
            dispatchLeftGameAction({
                type: SingleGameActionType.increaseGuessNumber
            });
        }
        else if (rightGame.currentGuessNumber < leftGame.currentGuessNumber) {
            dispatchRightGameAction({
                type: SingleGameActionType.increaseGuessNumber
            });
        }
    }, [leftGame.currentGuessNumber, rightGame.currentGuessNumber]);
    useEffect(() => {
        if (leftGame.state !== GameState.playing && rightGame.state !== GameState.playing) {
            if (gameType === GameType.practice) {
                setPracticeModalOpen(true);
            }
        }
    }, [leftGame.state, rightGame.state, setPracticeModalOpen]);
    return (_jsxs(Stack, { sx: {
            alignItems: "center",
            width: "100%",
            height: "100%",
        }, children: [_jsx(NewPracticeModal, { open: practiceModalOpen, handleClose: handlePracticeModalClose, handleNewPracticeGame: newPracticeGameListener, won: leftGame.state === GameState.won && rightGame.state === GameState.won, leftWord: leftGame.targetWord, rightWord: rightGame.targetWord }), _jsx(HeaderComponent, { newPracticeGameListener: newPracticeGameListener, dailyGameListener: dailyGameListener, gameType: gameType }), _jsx(DoubleGameComponent, { leftGame: leftGame, dispatchLeftGameAction: dispatchLeftGameAction, rightGame: rightGame, dispatchRightGameAction: dispatchRightGameAction })] }));
}
export default App;
